import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Icon,
  Modal,
  Tabs
} from "@theorgchartdev/storybook";
import cx from "classnames";
import React, { Fragment, useEffect, useRef, useState } from "react";
import { useTableManagerContext } from "@/providers/AppendTableProvider";
import CentralizedModalContainer from "@/appComponents/Blocks/Utils/CentralizedModalContainer/CentralizedModalContainer";
import { appstr } from "@coreapp/utils/appstr";
import { ModificationsTab } from "@/appComponents/Blocks/Modals/SettingsModal/ChartSettings/DataSourceConfiguration/DataSources/AppendTable/TableEditor/ModificationsTab";
import { ManageFieldsTab } from "@/appComponents/Blocks/Modals/SettingsModal/ChartSettings/DataSourceConfiguration/DataSources/AppendTable/TableEditor/ManageFieldsTab";
import { GeneralTab } from "@/appComponents/Blocks/Modals/SettingsModal/ChartSettings/DataSourceConfiguration/DataSources/AppendTable/TableEditor/GeneralTab";
import { GetWSMgr } from "@/Utils/StateLib";
import { appstrp } from "@coreapp/utils/appstrp";
import { useToast } from "@/providers/ToastProvider";
import { DATA_SOURCES_TEST_PREFIX } from "@/appComponents/Blocks/Modals/SettingsModal/ChartSettings/DataSourceConfiguration/Utils";

export const AppendTableEditorModal = () => {
  const {
    tableManager,
    setShowTableManager,
    tableEditorContextValue: { showTableEditor, onCloseTableEditor },
    recordEditorContextValue: { showRecordEditor }
  } = useTableManagerContext();

  const { addToast } = useToast();

  const tableMgr = tableManager.state;
  const appendTable = tableMgr?.fTableInfo;
  const appendTableRef = useRef(null);
  appendTableRef.current = appendTable;

  const [currentTab, setCurrentTab] = React.useState(0);
  const [isValid, setIsValid] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [currentFieldCount, setCurrentFieldCount] = useState(
    appendTable?.additionalFields?.length ?? 0
  );

  const modificationsRef = useRef();
  const manageFieldsRef = useRef();
  const generalRef = useRef();

  // Get current tab ref helper
  const getCurrentTabRef = () =>
    ({
      0: modificationsRef,
      1: manageFieldsRef,
      2: generalRef
    })[currentTab];

  useEffect(() => {
    setCurrentTab(0);
  }, [showTableEditor]);

  // Check both validity and unsaved changes when tab changes or content updates
  // Immediate check on tab change
  const checkTabState = async () => {
    const currentTabRef = getCurrentTabRef();
    if (!currentTabRef?.current) return;

    // Check validity
    if (currentTabRef.current?.checkValidity) {
      const validState = await currentTabRef.current.checkValidity();
      setIsValid(validState);
    }

    // Check unsaved changes
    if (currentTabRef.current?.hasUnsavedChanges) {
      const unsavedState = await currentTabRef.current.hasUnsavedChanges();
      setHasUnsavedChanges(unsavedState);
      updateFieldCount();
    }
  };

  // Continuous monitoring for changes within the current tab
  useEffect(() => {
    const interval = setInterval(checkTabState, 500);
    return () => clearInterval(interval);
  }, [currentTab]);

  useEffect(() => {
    updateFieldCount();
  }, [tableManager]);

  const handleSave = async (callbackFn = () => {}) => {
    try {
      const currentTabRef = getCurrentTabRef();

      if (!currentTabRef?.current) {
        return;
      }

      const isValid = await currentTabRef.current.validate();
      if (!isValid) {
        handleUnableToSave();
        setIsValid(false);
        return;
      }

      setIsSaving(true);

      // Add a slight delay before & saving to let unsaved changes update and child states settle
      await new Promise(resolve => setTimeout(resolve, 250));
      await currentTabRef.current.save();
      await new Promise(resolve => setTimeout(resolve, 250));

      addToast({ message: appstr("Changes have been saved"), status: "success" });

      if (typeof callbackFn === "function") {
        callbackFn();
      }
    } catch (error) {
      addToast({ message: appstr("An error occurred while saving"), status: "error" });
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveAlert = async callbackFn => {
    const currentTabRef = getCurrentTabRef();

    const isValid = await currentTabRef.current.validate();
    if (!isValid) {
      handleUnableToSave(callbackFn);
      setIsValid(false);
      return;
    }

    const wsMgr = GetWSMgr();
    wsMgr.CreateReactAlert(
      appstrp("Save Changes", "?"),
      appstr("genericsave"),
      async () => {
        await handleSave(callbackFn);
      },
      async () => {
        await handleDialogCancel(callbackFn);
      },
      appstr("Save"),
      appstr("Discard")
    );
  };

  const handleUnableToSave = callbackFn => {
    const wsMgr = GetWSMgr();
    wsMgr.CreateReactAlert(
      appstr("Unsaved changes detected"),
      appstr("Errors in form"),
      () => {},
      async () => {
        await handleDialogCancel(callbackFn);
      },
      appstr("Resolve"),
      appstr("Discard")
    );
  };

  const handleTabChange = async newTabIndex => {
    const currentTabRef = getCurrentTabRef();

    // Check if current tab has unsaved changes
    if (currentTabRef?.current?.hasUnsavedChanges) {
      const hasUnsaved = await currentTabRef.current.hasUnsavedChanges();
      if (hasUnsaved) {
        handleSaveAlert(() => setCurrentTab(newTabIndex));
        return;
      }
    }

    // If no unsaved changes, proceed with tab change
    setCurrentTab(newTabIndex);
    checkTabState();
  };

  const handleDialogCancel = callbackFn => {
    if (typeof callbackFn === "function") callbackFn();
  };

  const onClose = async (callbackFn = () => {}) => {
    const currentTabRef = getCurrentTabRef();
    const hasUnsaved = await currentTabRef.current.hasUnsavedChanges();
    if (hasUnsaved) {
      handleSaveAlert(() => {
        onCloseTableEditor();
        if (typeof callbackFn === "function") callbackFn();
      });
      return;
    }
    onCloseTableEditor();
    if (typeof callbackFn === "function") callbackFn();
  };

  const handleReturnToTableManager = async () => {
    await onClose(() => setShowTableManager(true));
  };

  const updateFieldCount = () => {
    const appendTable = appendTableRef.current;
    const savedFieldCount =
      (appendTable?.additionalFields?.length ?? 0) +
      (appendTable?.additionalPositionFields?.length ?? 0);
    const currentFieldCount = manageFieldsRef.current
      ? manageFieldsRef.current.modCount()
      : savedFieldCount;
    setCurrentFieldCount(currentFieldCount);
  };

  const modCount = tableMgr?.fOverrides?.length ?? 0;

  const badgeProps = {
    color: "success",
    size: "medium",
    overlap: "circular",
    border: false
  };

  const dataTestIdPrefix = "append-table-editor-";

  const tabs = [
    {
      header: appstr("Modifications"),
      content: (
        <div className={"oc-table-tab"}>
          <ModificationsTab
            ref={modificationsRef}
            tableMgr={tableMgr}
            onValidityChange={setIsValid}
          />
        </div>
      ),
      badgeProps: {
        ...badgeProps,
        showBadge: modCount > 0,
        badgeContent: modCount,
        tooltipProps: {
          message: modCount > 0 ? appstr("Number of modifications") : undefined,
          position: "right"
        }
      },
      dataTestId: `${dataTestIdPrefix}modifications`
    },
    {
      header: appstr("Manage fields"),
      content: (
        <div className={"oc-table-tab"}>
          <ManageFieldsTab
            ref={manageFieldsRef}
            tableMgr={tableManager}
            onValidityChange={setIsValid}
            onFieldChange={updateFieldCount}
          />
        </div>
      ),
      badgeProps: {
        ...badgeProps,
        showBadge: currentFieldCount > 0,
        badgeContent: currentFieldCount,
        tooltipProps: {
          message: currentFieldCount > 0 ? appstr("Number of additional fields") : undefined,
          position: "right"
        }
      },
      dataTestId: `${dataTestIdPrefix}manage-fields`
    },
    {
      header: appstr("General"),
      content: (
        <div className={"oc-table-tab"}>
          <GeneralTab ref={generalRef} tableMgr={tableMgr} onValidityChange={setIsValid} />
        </div>
      ),
      dataTestId: `${dataTestIdPrefix}general`
    }
  ];

  return (
    <Fragment>
      <CentralizedModalContainer show={showTableEditor}>
        <Modal
          className={cx({
            "oc-pointer-events-none": showRecordEditor
          })}
          id={"append-table-editor"}
          dataTestId={`${DATA_SOURCES_TEST_PREFIX}-append-table-editor-modal`}
          show={showTableEditor}
          onClose={onClose}
          minWidth={"large"}
          maxWidth={"large"}
          fullScreen={true}
          withShadow={true}
          centerScreen={true}
        >
          <DialogTitle
            divider={true}
            onClose={onClose}
            data-testid={`${DATA_SOURCES_TEST_PREFIX}-append-table-editor-modal-title`}
          >
            <span className={"ocn-explorer-icon-label"}>
              <Icon size={"medium"} name={"table"} />
              {tableMgr?.fTableInfo?.name}
            </span>
          </DialogTitle>
          <DialogContent customContentClass={"ocn-chart-explorer-content"}>
            <div className="oc-table-editor-container">
              <div
                className={cx(
                  "ocn-chart-explorer-column",
                  "ocn-chart-explorer-tabular",
                  "oc-tab-padding"
                )}
              >
                <Tabs
                  tabIndex={parseInt(currentTab)}
                  customClass={"ocn-chart-explorer-tabs"}
                  customContentClass={"ocn-chart-explorer-tabs-content"}
                  tabs={tabs}
                  onTabChange={handleTabChange}
                />
              </div>
            </div>
          </DialogContent>
          <DialogActions divider={true}>
            <div className={"oc-left-justify"}>
              <DialogActions.Secondary
                onClick={handleReturnToTableManager}
                dataTestId={`${dataTestIdPrefix}manage-tables`}
                iconProps={{ name: "arrow-left-solid" }}
              >
                {appstr("Manage Tables")}
              </DialogActions.Secondary>
            </div>
            <DialogActions.Secondary
              onClick={onClose}
              dataTestId={`${dataTestIdPrefix}close`}
              disabled={isSaving}
            >
              {appstr("Close")}
            </DialogActions.Secondary>
            <DialogActions.Primary
              onClick={handleSave}
              dataTestId={`${dataTestIdPrefix}save`}
              disabled={!isValid || !hasUnsavedChanges || isSaving}
              loading={isSaving}
            >
              {appstr("Save")}
            </DialogActions.Primary>
          </DialogActions>
        </Modal>
      </CentralizedModalContainer>
    </Fragment>
  );
};
